import { Overlay } from "ol";
import { Map } from "ol";
import { Coordinate } from "ol/coordinate";

/**
 * フォント
 */
const MENUITEM_FONT_FAMILY = "メイリオ";

/**
 * フォントサイズ定義値
 */
const MENUITEM_FONT_SIZE = "10pt";

/**
 * コンテキストメニュー表示クラス
 */
export class ContextMenu {
  /**
   * コンテキストメニュー用DOM
   */
  private _rootElement: HTMLElement;

  /**
   * 地図クラス
   */
  private _map: Map;

  /**
   * コンテキストメニュー用オーバレイクラス
   */
  private _menuOverlay: Overlay;

  /**
   * 表示状態
   */
  public _isShow: boolean;

  /**
   * コンストラクタ
   * @param map 地図クラス
   */
  public constructor(map: Map) {
    this._map = map;
    this._menuOverlay = this.createOverLay();
    this._map.addOverlay(this._menuOverlay);
  }

  /**
   * 指定した位置にコンテキストメニューを表示する。
   * @param position システム座標
   */
  public show(position: Coordinate) {
    //閉じるボタンの追加
    const border = document.createElement("hr");
    border.style.marginTop = "1px";
    border.style.marginBottom = "3px";
    this._rootElement.appendChild(border);
    this.addMenuItem("閉じる", () => {
      this.close();
    });

    //Overlayrのデフォルト位置の指定
    this._menuOverlay.setPositioning("top-left");
    this._menuOverlay.setPosition(position);
    this._isShow = true;

    //地図画面サイズの取得
    const mapClientRect = this._map.getViewport().getBoundingClientRect();

    //コンテキストメニューサイズの取得
    const menuClientRect = this._rootElement.getBoundingClientRect();

    //地図表示部の縦の中心点を取得
    const centerY = (mapClientRect.bottom - mapClientRect.y) / 2;

    //メニューの表示位置が地図の中央より上か下かで指定位置の下向きにコンテキストを表示するか、
    //上向きに表示するか判定する。
    let isUpper = false;
    if (menuClientRect.y > centerY) {
      isUpper = true;
      const verticalDiff = menuClientRect.y - mapClientRect.top - 50;
      this._rootElement.style.maxHeight = verticalDiff + "px";
    } else {
      isUpper = false;
      const verticalDiff = mapClientRect.bottom - menuClientRect.y - 50;
      this._rootElement.style.maxHeight = verticalDiff + "px";
    }

    //コンテキストメニューの左が地図表示部の左よりも左側の座標の場合はクリック地点の
    //右側にコンテキストメニューを表示する。
    if (mapClientRect.left > menuClientRect.left) {
      if (isUpper) {
        this._menuOverlay.setPositioning("bottom-left");
      } else {
        this._menuOverlay.setPositioning("top-left");
      }
      //コンテキストメニューの右側が地図表示部の右よりも右側の座標の場合はクリック地点の
      //左側にコンテキストメニューを表示する。
    } else if (mapClientRect.right < menuClientRect.right) {
      if (isUpper) {
        this._menuOverlay.setPositioning("bottom-right");
      } else {
        this._menuOverlay.setPositioning("top-right");
      }
      //コンテキストメニューの左も右も地図表示部の範囲内の場合は右側にコンテキストメニューを表示
    } else {
      if (isUpper) {
        this._menuOverlay.setPositioning("bottom-left");
      } else {
        this._menuOverlay.setPositioning("top-left");
      }
    }
    this._menuOverlay.setPosition(position);
    this._isShow = true;
  }

  /**
   * コンテキストメニューを閉じる。
   */
  public close() {
    this._menuOverlay.setPosition(null);
    this._rootElement.innerHTML = "";
    this._isShow = false;
  }

  /**
   * コンテキストメニュー用Overlayを作成・取得する。
   * @returns コンテキストメニュー用Overlay
   */
  private createOverLay(): Overlay {
    this._rootElement = document.createElement("div");
    this._rootElement.style.overflow = "auto";
    this._rootElement.style.border = "1px solid rgba(218,220,224,1.0)";
    this._rootElement.style.paddingBottom = "6px";
    this._rootElement.style.paddingTop = "6px";
    this._rootElement.style.backgroundColor = "#FFFFFF";

    const menu = new Overlay({
      element: this._rootElement,
      positioning: "top-right",
      stopEvent: false,
    });
    this._rootElement.onmouseout = this.mouseOut.bind(this);
    return menu;
  }

  /**
   * コンテキストメニューのマウスオーバ処理
   * マウスオーバしたメニューアイテムの色を変更する。
   * @param menuItem マウスオーバされたコンテキストメニュー
   */
  private mouseOver(menuItem: HTMLElement): void {
    const nodes = this._rootElement.children;
    for (const node of nodes) {
      const htmlDiv = <HTMLDivElement>node;
      htmlDiv.style.backgroundColor = "#FFFFFF";
      htmlDiv.style.color = "#000000";
    }
    menuItem.style.backgroundColor = "#005984";
    menuItem.style.color = "#FFFFFF";
  }

  /**
   * コンテキストメニューからマウスが離れた場合の処理
   * マウスオーバの色をもとに戻す。
   */
  private mouseOut(): void {
    const nodes = this._rootElement.children;
    for (const node of nodes) {
      const htmlDiv = <HTMLDivElement>node;
      htmlDiv.style.backgroundColor = "#FFFFFF";
      htmlDiv.style.color = "#000000";
    }
  }

  /**
   * コンテキストメニューにメニュー項目を追加する。
   * @param txtName 表示名称
   * @param clickMenuItemAction クリック時アクション
   */
  public addMenuItem(
    txtName: string,
    clickMenuItemAction: () => void,
    id?: string
  ) {
    const menuItem = document.createElement("div");
    menuItem.textContent = txtName;
    menuItem.style.backgroundColor = "#FFFFFF";
    menuItem.style.paddingLeft = "7pt";
    menuItem.style.paddingRight = "7pt";
    menuItem.style.paddingBottom = "1pt";
    menuItem.style.width = "auto";
    menuItem.style.height = "auto";
    menuItem.style.fontSize = MENUITEM_FONT_SIZE;
    menuItem.style.fontFamily = MENUITEM_FONT_FAMILY;
    menuItem.id = id ?? "contextMenuItem";
    menuItem.style.cursor = "default";
    this._rootElement.appendChild(menuItem);
    menuItem.onmouseover = () => {
      this.mouseOver(menuItem);
    };
    menuItem.onclick = () => {
      this._menuOverlay.setPosition(null);
      clickMenuItemAction();
      this.close();
    };
  }
}
